class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.searchInput = null;
    this.resultsContainer = null;
    this.searchTimeout = null;
    this.isLoading = false;
    this.currentQuery = '';
    this.debounceDelay = 300;
    this.minQueryLength = 2;
    this.sectionId = this.getAttribute('section-id') || 'predictive-search-results';
    this.motion = null;
    this.defaultContent = null;
  }

  connectedCallback() {
    this.setupElements();
    this.setupEventListeners();
    this.waitForMotion();
    this.loadDefaultContent();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  waitForMotion() {
    whenReady('motion', (motion) => {
      this.motion = motion;
    });
  }

  loadDefaultContent() {
    // Get the default content directly from the existing DOM
    const defaultDiv = document.getElementById('predictive-search-default');
    
    if (defaultDiv) {
      this.defaultContent = defaultDiv.innerHTML;
      // Show default content initially if no search query
      if (!this.currentQuery) {
        this.displayDefaultContent();
      }
    }
  }

  setupElements() {
    // Find the search input within this component
    this.searchInput = this.querySelector('input[type="search"], input[name*="search"], input[name*="q"]');
    
    // Find existing results container
    this.resultsContainer = this.querySelector('.predictive-search__results');
    
    // Find the form
    this.form = this.querySelector('form') || this.searchInput?.closest('form');
    
    if (!this.searchInput) {
      console.warn('PredictiveSearch: No search input found within the component');
      return;
    }

    // Set up ARIA attributes
    this.searchInput.setAttribute('aria-expanded', 'false');
    this.searchInput.setAttribute('aria-controls', 'predictive-search-results');
    this.searchInput.setAttribute('aria-describedby', 'predictive-search-status');
  }

  setupEventListeners() {
    if (!this.searchInput) return;

    // Input events
    this.searchInput.addEventListener('input', this.handleInput.bind(this));
    this.searchInput.addEventListener('focus', this.handleFocus.bind(this));
    // this.searchInput.addEventListener('blur', this.handleBlur.bind(this));
    this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    // Escape key to close
    document.addEventListener('keydown', this.handleEscape.bind(this));
  }

  handleInput(event) {
    const query = event.target.value.trim();
    this.currentQuery = query;

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Show default content if query is too short
    if (query.length < this.minQueryLength) {
      this.displayDefaultContent();
      return;
    }

    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceDelay);
  }

  handleFocus() {
    // Always show results on focus (either default or search results)
    this.showResults();
    if (this.currentQuery.length < this.minQueryLength) {
      this.displayDefaultContent();
    }
  }

//   handleBlur() {
//     // Delay hiding to allow for clicks on results
//     setTimeout(() => {
//       if (!this.matches(':focus-within')) {
//         this.hideResults();
//       }
//     }, 150);
//   }

  handleKeydown(event) {
    const results = this.querySelectorAll('.predictive-search__item');
    const currentIndex = Array.from(results).findIndex(item => item === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateResults(currentIndex, 1, results);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateResults(currentIndex, -1, results);
        break;
      case 'Enter':
        if (document.activeElement.classList.contains('predictive-search__item')) {
          event.preventDefault();
          document.activeElement.click();
        }
        break;
      case 'Escape':
        this.hideResults();
        this.searchInput.blur();
        break;
    }
  }

  navigateResults(currentIndex, direction, results) {
    if (results.length === 0) return;

    let nextIndex;
    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : results.length - 1;
    } else {
      nextIndex = (currentIndex + direction + results.length) % results.length;
    }

    results[nextIndex].focus();
  }

  handleSubmit(event) {
    event.preventDefault();
    const query = this.searchInput.value.trim();
    
    if (query) {
      this.navigateToSearchPage(query);
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.resultsContainer) {
      this.hideResults();
      this.searchInput.blur();
    }
  }

  async performSearch(query) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    try {

      const response = await fetch(`${window.location.origin}/search?q=${encodeURIComponent(query)}&view=data&predictive=true`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      let parser = new DOMParser();
      let doc = parser.parseFromString(html, 'text/html');

      this.displayResults(html, query);
    } catch (error) {
      console.error('Predictive search error:', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  displayDefaultContent() {
    const contentContainer = this.querySelector('.predictive-search__content');
    if (!contentContainer || !this.defaultContent) return;

    // Don't re-render if content is already showing the default
    if (contentContainer.innerHTML === this.defaultContent && contentContainer.style.display !== 'none') {
      return;
    }

    contentContainer.innerHTML = this.defaultContent;
    this.showElement(contentContainer);
    this.updateStatus('Default content loaded');
  }

  displayResults(html, query) {
    // Find existing content container
    const contentContainer = this.querySelector('.predictive-search__content');
    const querySpan = this.querySelector('.predictive-search__query');

    if (!contentContainer) return;

    // Don't re-render if content is already the same
    if (contentContainer.innerHTML === html && contentContainer.style.display !== 'none') {
      return;
    }

    // Clear previous results
    contentContainer.innerHTML = '';

    // Check if the HTML contains actual results
    const hasResults = html.trim().length > 0 && !html.includes('search-grid-empty');

    // Always show the fetched content first
    contentContainer.innerHTML = html;
    
    if (hasResults) {
      this.updateStatus('Search results loaded');
    } else {
      // Append default content after the fetched content when no results
      if (this.defaultContent) {
        contentContainer.innerHTML += this.defaultContent;
        this.updateStatus('No results found, showing default content');
      } else {
        this.updateStatus('No results found');
      }
    }

    this.showElement(contentContainer);
  }

  showResults() {
    if (this.resultsContainer) {
      // Don't re-show if already visible
      if (this.resultsContainer.style.display !== 'none' && this.resultsContainer.style.opacity !== '0') {
        return;
      }
      this.showElement(this.resultsContainer);
    }
    if (this.searchInput) {
      this.searchInput.setAttribute('aria-expanded', 'true');
    }
  }

  hideResults() {
    if (this.resultsContainer) {
      this.hideElement(this.resultsContainer);
    }
    if (this.searchInput) {
      this.searchInput.setAttribute('aria-expanded', 'false');
    }
  }

  showLoading() {
    const loading = this.querySelector('.predictive-search__loading');
    if (loading) {
      this.showElement(loading);
    }
    this.updateStatus('Searching...');
  }

  hideLoading() {
    const loading = this.querySelector('.predictive-search__loading');
    if (loading) {
      this.hideElement(loading);
    }
  }

  showElement(element) {
    if (this.motion) {
      this.motion.animate(element, { display: 'flex', flexDirection: 'column' }, { duration: 0 });
      this.motion.animate(element, { opacity: 1 }, { 
        duration: 0.5, 
        easing: [0.25, 0.46, 0.45, 0.94] // power4.inOut equivalent
      });
    } else {
      element.style.display = 'flex';
      element.style.flexDirection = 'column';
      element.style.opacity = '1';
    }
  }

  hideElement(element) {
    if (this.motion) {
      this.motion.animate(element, {
        opacity: 0
      }, {
        duration: 0.3,
        easing: [0.25, 0.46, 0.45, 0.94] // power4.inOut equivalent
      }).finished.then(() => {
        this.motion.animate(element, { display: 'none' }, { duration: 0 });
      });
    } else {
      element.style.display = 'none';
      element.style.opacity = '0';
    }
  }

  showError() {
    this.updateStatus('Search error occurred');
  }

  updateStatus(message) {
    let status = this.querySelector('#predictive-search-status');
    if (!status) {
      status = document.createElement('div');
      status.id = 'predictive-search-status';
      status.className = 'sr-only';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      this.appendChild(status);
    }
    status.textContent = message;
  }

  navigateToSearchPage(query) {
    const searchUrl = `/search?q=${encodeURIComponent(query)}`;
    window.location.href = searchUrl;
  }

  cleanup() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
}

// Register the web component
customElements.define('predictive-search', PredictiveSearch);
